import { Button, Icon } from '@faxi/web-component-library';
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { Slider, ValueCurrencyBox } from 'components';
import env from 'env';
import parse from 'html-react-parser';
import { FinanceCalculationFormFields } from 'models';
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import storageService from 'services';

import { defaultMutationFn } from '../../../../config/api/queryClient';
import { CARS_AVERAGE_COST_VALUE } from '../../../../constants/carsAverageCostValue';
import { breakTranslationNewlines, formatMoneyValue } from '../../../../utils';
import LegalNoteModal from '../ValueOfYourCarForm/components/LegalNoteModal.component';
import { StyledFinanceCalculationForm } from './FinanceCalculationForm.styled';

export type FinanceCalculationFormProps = {
  changeStep: Dispatch<SetStateAction<number>>;
};

type FinanceApiData = {
  canone_mensile_lordo: number;
  tan: number;
  taeg: number;
  importo_residuo_lordo: number;
};

const FinanceCalculationForm: FC<FinanceCalculationFormProps> = props => {
  const { changeStep } = props;

  const { t } = useTranslation();

  const [initialData, setInitialData] =
    useState<FinanceCalculationFormFields>();

  const [term, setTerm] = useState<number>(
    +(
      storageService.getItem(
        env.VITE_FORM_STORAGE_KEY
      ) as FinanceCalculationFormFields
    )?.term || 24
  );

  const price = useMemo(
    () =>
      +(
        storageService.getItem(
          env.VITE_FORM_STORAGE_KEY
        ) as FinanceCalculationFormFields
      )?.price,
    []
  );

  const { data, mutateAsync, isPending, isError } = useMutation<
    FinanceApiData,
    any,
    {
      duration: number;
      price: number;
    }
  >({
    mutationKey: ['create-offer'],
    mutationFn: v => defaultMutationFn('create-offer', v),
  });

  const handleSubmit = useCallback(async () => {
    try {
      if (!data) return;
      Object.keys(data).forEach(key => {
        storageService.updateItem(
          key,
          data[key as keyof FinanceApiData].toString()
        );
      });

      storageService.updateItem('activeStep', '3');

      changeStep(old => old + 1);
    } catch (e) {
      console.error(e);
    }
  }, [data, changeStep]);

  useEffect(() => {
    setInitialData(
      storageService.getItem(env.VITE_FORM_STORAGE_KEY) ||
        ({} as FinanceCalculationFormFields)
    );
  }, []);

  useEffect(() => {
    const debounceTimeout = setTimeout(async () => {
      await mutateAsync({
        duration: term,
        price,
      });
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [term, mutateAsync, price]);

  const loadingText = useMemo(() => `${t('ced-loading')}...`, [t]);

  const carAverageCostValue =
    CARS_AVERAGE_COST_VALUE[
      initialData?.model as keyof typeof CARS_AVERAGE_COST_VALUE
    ];

  return (
    <StyledFinanceCalculationForm
      className={classNames(
        'cedolauto-finance-calculation-form',
        'cedolauto-application__form'
      )}
      onSubmit={handleSubmit}
      initialData={initialData}
    >
      <div className="cedolauto-application__form__wrapper">
        <div className="cedolauto-application__form__wrapper__fields">
          <div className="cedolauto-application__form__wrapper__fields__finance-data">
            {t('ced-application-tab1-title')}
            <span
              className={classNames(
                'cedolauto-application__form__wrapper__fields__finance-data__value'
              )}
            >
              {formatMoneyValue(Number(initialData?.price || 0))}
            </span>
          </div>
          <div className="cedolauto-application__form__wrapper__fields__finance-data">
            {t('ced-application-tab2-tan-label')}
            <span
              className={classNames(
                'cedolauto-application__form__wrapper__fields__finance-data__value',
                {
                  'cedolauto-application__form__wrapper__fields__finance-data__value--error':
                    isError,
                }
              )}
            >
              {data?.tan
                ? data.tan.toFixed(2)
                : isError
                  ? t('ced-error')
                  : loadingText}
            </span>
          </div>
          <div className="cedolauto-application__form__wrapper__fields__finance-data">
            {t('ced-application-tab2-taeg-label')}
            <span
              className={classNames(
                'cedolauto-application__form__wrapper__fields__finance-data__value',
                {
                  'cedolauto-application__form__wrapper__fields__finance-data__value--error':
                    isError,
                }
              )}
            >
              {data?.taeg
                ? data.taeg.toFixed(2)
                : isError
                  ? t('ced-error')
                  : loadingText}
            </span>
          </div>
          <div className="cedolauto-application__form__wrapper__fields__finance-data">
            {t('ced-application-tab2-residual-label')}
            <span
              className={classNames(
                'cedolauto-application__form__wrapper__fields__finance-data__value',
                {
                  'cedolauto-application__form__wrapper__fields__finance-data__value--error':
                    isError,
                }
              )}
            >
              {data?.importo_residuo_lordo
                ? formatMoneyValue(data.importo_residuo_lordo)
                : isError
                  ? t('ced-error')
                  : loadingText}
            </span>
          </div>
          <LegalNoteModal />
        </div>

        <div className="cedolauto-finance-calculation-form__term">
          <div className="cedolauto-finance-calculation-form__term__title">
            {t('section_5-step_2-form-card-title')}
          </div>
          <p className="cedolauto-finance-calculation-form__term__desc">
            {t('section_5-step_2-form-card-subtitle')}
          </p>
          <div>
            <Slider
              value={term}
              step={12}
              onChange={async e => {
                if (!isPending) {
                  setTerm(+e.target.value);
                }
              }}
              min={12}
              max={60}
            />
            <div className="cedolauto-finance-calculation-form__term__value">
              {t('section_5-step_2-form-card-date', { number: term })}
            </div>
          </div>
        </div>
      </div>
      <ValueCurrencyBox
        values={[
          {
            title: t('section_5-step_2-text_1'),
            value: data?.canone_mensile_lordo
              ? formatMoneyValue(data.canone_mensile_lordo)
              : isError
                ? t('ced-error_try_again')
                : loadingText,
          },
          ...(carAverageCostValue &&
          (!data?.canone_mensile_lordo ||
            data?.canone_mensile_lordo < carAverageCostValue)
            ? [
                {
                  title: t('section_5-step_2-text_2'),
                  value: formatMoneyValue(carAverageCostValue),
                  tooltipInfo: t('section_5-step_2-tooltip-text'),
                },
              ]
            : []),
        ]}
        info={parse(
          breakTranslationNewlines(
            [
              t('step_2-sidetext-line_1'),
              t('step_2-sidetext-line_2'),
              t('step_2-sidetext-line_3'),
            ].join('\\n')
          )
        )}
      />

      <div className="cedolauto-finance-calculation-form__actions">
        <Button
          variant="ghost"
          className="cedolauto-finance-calculation-form__actions__prev-step"
          onClick={() => {
            changeStep(old => old - 1);
          }}
          icon={<Icon name="chevron-down" />}
        >
          {t('ced-action-previous-step')}
        </Button>

        <Button
          variant="outline"
          type="submit"
          className="cedolauto-application__form__submit"
          disabled={isPending}
        >
          {parse(t('ced-action-continue'))}
        </Button>
      </div>
    </StyledFinanceCalculationForm>
  );
};

export default FinanceCalculationForm;
